import type { RootState } from '..';
import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import type { ConfigurationUserInputs, UserInputsByMetadataId } from '@server/user-inputs';
import type {
  DocumentationTagAndMetadataElementsDto,
  DocumentationUserAndMetadataElementsDto,
  TagDto,
} from '../../types/documentationUserInputs';
import { UserInputTypes } from '@server/user-inputs';
import type { DocumentationTabTypes } from '../../types/enums/DocumentationTabTypes';
import type { DocumentationItemsMap } from './createDocumentationItemsMap';
import uniq from 'lodash/uniq';
import { ConfigurationTypeToDocumentationTabType } from '../../types/enums/DocumentationTabTypes';
import {
  addSalesforceItemToConfigurationItemMap,
  buildTagsAndUsersCountSummaryFromTagConfigurations,
  removeMetadataElementIdFromConfigurationItemMap,
} from '../../components/documentation/tags-center/countsFromMetadataElements';
import { findConfigurationType } from './summaryUtils';
import type { ConfigurationItemMap } from '../../components/documentation/configuration-item-tables/shared/helpers';
import type { SalesforceItem } from '../../components/parser/ParserTypes';
import {
  buildUserInputsByMetadataId,
  tagConfigurationsForTagFromApi,
  tagConfigurationsForUserFromApi,
  tagEntityForTagsList,
  userEntriesFromTagsAndUsersApi,
} from './userInputsReducerUtils';

export type UserInputsUserListEntry = Pick<DocumentationUserAndMetadataElementsDto, 'userId'>;

const emptyTagUserConfigurations = (): {
  [tagKey in UserInputTypes]: ConfigurationItemMap;
} => ({
  [UserInputTypes.Tags]: {},
  [UserInputTypes.Owner]: {},
  [UserInputTypes.Collaborators]: {},
});

const ensureTagUserConfigurations = (
  state: UserInputsState,
  tagId: string,
): { [tagKey in UserInputTypes]: ConfigurationItemMap } => {
  if (!state.tagsConfigurations[tagId]) {
    state.tagsConfigurations[tagId] = emptyTagUserConfigurations();
  }
  return state.tagsConfigurations[tagId];
};

/**
 * User inputs are combination of tags and users (as collaborators owners)
 */

export interface UserInputsState {
  tagsList: TagDto[];
  usersWithMetadataList: UserInputsUserListEntry[];
  tagsConfigurations: {
    [tagId: string]: { [tagKey in UserInputTypes]: ConfigurationItemMap };
  };
  userInputsByMetadataId?: UserInputsByMetadataId;
  tagsAndUsersCountSummary?: {
    [crmOrgId: string]: {
      [tagOrUserId: string]: {
        [configurationType in Exclude<
          DocumentationTabTypes,
          DocumentationTabTypes.CARDS_LIST | DocumentationTabTypes.RELATED_OBJECTS
        >]: string[];
      };
    };
  };
  parserItemsMap?: { [crmOrgId: string]: DocumentationItemsMap };
}

const initialState: UserInputsState = {
  tagsList: [],
  usersWithMetadataList: [],
  tagsConfigurations: {},
};

export const userInputsSlice = createSlice({
  name: 'userInputs',
  initialState,
  reducers: {
    applyUserInputsFromTagsAndUsersApi: (
      state,
      action: PayloadAction<{
        crmOrgId: string;
        tags: DocumentationTagAndMetadataElementsDto[];
        users: DocumentationUserAndMetadataElementsDto[];
      }>,
    ) => {
      const { crmOrgId, tags, users } = action.payload;
      state.tagsList = tags.map(tagEntityForTagsList);
      state.usersWithMetadataList = userEntriesFromTagsAndUsersApi(users);
      state.userInputsByMetadataId = buildUserInputsByMetadataId(tags, users);
      for (const tag of tags) {
        state.tagsConfigurations[tag.id] = tagConfigurationsForTagFromApi(tag);
      }
      for (const user of users) {
        state.tagsConfigurations[user.userId] = tagConfigurationsForUserFromApi(user);
      }
      if (!state.tagsAndUsersCountSummary) {
        state.tagsAndUsersCountSummary = {};
      }
      state.tagsAndUsersCountSummary[crmOrgId] = buildTagsAndUsersCountSummaryFromTagConfigurations(
        state.tagsConfigurations,
      ) as any;
    },
    setTagsList: (state, action: PayloadAction<{ tags: TagDto[] }>) => {
      state.tagsList = action.payload.tags;
    },
    setUsersWithMetadataList: (
      state,
      action: PayloadAction<{ users: UserInputsUserListEntry[] }>,
    ) => {
      state.usersWithMetadataList = action.payload.users;
    },
    setTagConfigurations: (
      state,
      action: PayloadAction<{
        tagId: string;
        configurations: { [key in UserInputTypes]: ConfigurationItemMap };
      }>,
    ) => {
      const { configurations, tagId } = action.payload;
      state.tagsConfigurations[tagId] = configurations;
    },
    clearTagConfigurations: (state) => {
      state.tagsConfigurations = {};
      state.usersWithMetadataList = [];
    },
    addTag: (state, action: PayloadAction<{ tag: TagDto }>) => {
      state.tagsList.push(action.payload.tag);
    },
    updateTag: (state, action: PayloadAction<{ tag: TagDto }>) => {
      const { tag } = action.payload;
      state.tagsList = state.tagsList.map((_tag) => (_tag.id === tag.id ? tag : _tag));
    },
    deleteTag: (state, action: PayloadAction<{ tagId: string }>) => {
      const { tagId } = action.payload;
      state.tagsList = state.tagsList.filter((tag) => tag.id !== tagId);
    },
    setUserInputsByMetadataId: (
      state,
      action: PayloadAction<{
        userInputs: UserInputsByMetadataId;
      }>,
    ) => {
      state.userInputsByMetadataId = action.payload.userInputs;
    },
    setSingleConfigurationUserInputs: (
      state,
      action: PayloadAction<{
        userInputs: UserInputsByMetadataId;
        crmOrgId: string;
      }>,
    ) => {
      const { userInputs, crmOrgId } = action.payload;
      const [[metadataId, userInputData]] = Object.entries(userInputs);
      if (!state.userInputsByMetadataId) {
        state.userInputsByMetadataId = {};
      }

      // Remove old configuration from summary before updating
      if (state.userInputsByMetadataId[metadataId] && state.tagsAndUsersCountSummary?.[crmOrgId]) {
        const summary = state.tagsAndUsersCountSummary[crmOrgId];
        Object.keys(summary).forEach((tagId) => {
          Object.keys(summary[tagId]).forEach((tabType) => {
            const configArray = (summary[tagId] as any)[tabType] as string[];
            const index = configArray.indexOf(metadataId);
            if (index > -1) {
              configArray.splice(index, 1);
            }
          });
        });
      }

      state.userInputsByMetadataId[metadataId] = userInputData;

      // Add updated configuration to summary
      const parserItemsMap = state.parserItemsMap;
      if (parserItemsMap?.[crmOrgId]) {
        const parserItems = parserItemsMap[crmOrgId];
        const configurationType = findConfigurationType(parserItems, metadataId);

        if (configurationType) {
          const documentationTabType = ConfigurationTypeToDocumentationTabType[configurationType];
          if (documentationTabType) {
            const allTagIds = [
              ...userInputData[UserInputTypes.Tags],
              ...(userInputData[UserInputTypes.Owner] ? [userInputData[UserInputTypes.Owner]] : []),
              ...userInputData[UserInputTypes.Collaborators],
            ].filter(Boolean);

            if (!state.tagsAndUsersCountSummary) {
              state.tagsAndUsersCountSummary = {};
            }
            if (!state.tagsAndUsersCountSummary[crmOrgId]) {
              state.tagsAndUsersCountSummary[crmOrgId] = {};
            }

            allTagIds.forEach((tagId) => {
              if (!state.tagsAndUsersCountSummary![crmOrgId][tagId]) {
                state.tagsAndUsersCountSummary![crmOrgId][tagId] = {} as any;
              }
              if (
                !(state.tagsAndUsersCountSummary![crmOrgId][tagId] as any)[documentationTabType]
              ) {
                (state.tagsAndUsersCountSummary![crmOrgId][tagId] as any)[documentationTabType] =
                  [];
              }

              const configArray = (state.tagsAndUsersCountSummary![crmOrgId][tagId] as any)[
                documentationTabType
              ] as string[];
              if (!configArray.includes(metadataId)) {
                configArray.push(metadataId);
              }
            });
          }
        }
      }
    },

    deleteTagFromConfigurationUserInputs: (
      state,
      action: PayloadAction<{
        tagId: string;
        crmOrgId: string;
      }>,
    ) => {
      const { tagId, crmOrgId } = action.payload;
      const userInputsByMetadataId = state.userInputsByMetadataId ?? ({} as UserInputsByMetadataId);

      const filteredUserInputsByMetadataId = Object.entries(userInputsByMetadataId).reduce(
        (acc, [key]) => {
          const typedKey = key as keyof UserInputsByMetadataId;
          const itemsOfKey = userInputsByMetadataId[typedKey];
          const currentTags = itemsOfKey[UserInputTypes.Tags];
          const currentCollaborators = itemsOfKey[UserInputTypes.Collaborators];
          const currentOwner = itemsOfKey[UserInputTypes.Owner];

          acc[typedKey] = {
            [UserInputTypes.Tags]: currentTags.filter((itemId: string) => itemId !== tagId),
            [UserInputTypes.Collaborators]: currentCollaborators.filter(
              (itemId: string) => itemId !== tagId,
            ),
            [UserInputTypes.Owner]: currentOwner === tagId ? '' : currentOwner,
          };
          return acc;
        },
        {} as UserInputsByMetadataId,
      );

      state.userInputsByMetadataId = filteredUserInputsByMetadataId;

      if (state.tagsAndUsersCountSummary?.[crmOrgId]) {
        delete state.tagsAndUsersCountSummary[crmOrgId][tagId];
      }

      if (state.tagsConfigurations[tagId]) {
        delete state.tagsConfigurations[tagId];
      }
    },
    setParserItemsMap: (
      state,
      action: PayloadAction<{ parserItemsMap: DocumentationItemsMap; crmOrgId: string }>,
    ) => {
      state.parserItemsMap = {
        ...state.parserItemsMap,
        [action.payload.crmOrgId]: action.payload.parserItemsMap,
      };
    },
    generateTagsAndUsersSummary: (state, action: PayloadAction<{ crmOrgId: string }>) => {
      const { crmOrgId } = action.payload;

      if (!state.tagsAndUsersCountSummary) {
        state.tagsAndUsersCountSummary = {};
      }

      state.tagsAndUsersCountSummary[crmOrgId] = buildTagsAndUsersCountSummaryFromTagConfigurations(
        state.tagsConfigurations,
      ) as any;
    },

    patchUserInputsMetadataForSavedConfiguration: (
      state,
      action: PayloadAction<{
        next: ConfigurationUserInputs;
        item: SalesforceItem;
      }>,
    ) => {
      const { next, item } = action.payload;
      const metadataElementId = item.metadataId;
      if (!metadataElementId) return;

      const previous: ConfigurationUserInputs = state.userInputsByMetadataId?.[
        metadataElementId
      ] ?? {
        [UserInputTypes.Tags]: [],
        [UserInputTypes.Owner]: '',
        [UserInputTypes.Collaborators]: [],
      };

      const prevTags = previous[UserInputTypes.Tags];
      const nextTags = next[UserInputTypes.Tags];
      const addedTagIds = nextTags.filter((id) => !prevTags.includes(id));
      const removedTagIds = prevTags.filter((id) => !nextTags.includes(id));

      for (const tagId of addedTagIds) {
        const tagConfigs = ensureTagUserConfigurations(state, tagId);
        addSalesforceItemToConfigurationItemMap(tagConfigs[UserInputTypes.Tags], item);
      }
      for (const tagId of removedTagIds) {
        const configs = state.tagsConfigurations[tagId];
        if (configs) {
          removeMetadataElementIdFromConfigurationItemMap(
            configs[UserInputTypes.Tags],
            metadataElementId,
          );
        }
      }

      const prevOwner = previous[UserInputTypes.Owner];
      const nextOwner = next[UserInputTypes.Owner];
      if (prevOwner !== nextOwner) {
        if (prevOwner) {
          const prevCfg = state.tagsConfigurations[prevOwner];
          if (prevCfg) {
            removeMetadataElementIdFromConfigurationItemMap(
              prevCfg[UserInputTypes.Owner],
              metadataElementId,
            );
          }
        }
        if (nextOwner) {
          ensureTagUserConfigurations(state, nextOwner);
          addSalesforceItemToConfigurationItemMap(
            state.tagsConfigurations[nextOwner][UserInputTypes.Owner],
            item,
          );
          if (!state.usersWithMetadataList.some((x) => x.userId === nextOwner)) {
            state.usersWithMetadataList.push({ userId: nextOwner });
          }
        }
      }

      const prevColl = previous[UserInputTypes.Collaborators];
      const nextColl = next[UserInputTypes.Collaborators];
      const addedColl = nextColl.filter((id) => !prevColl.includes(id));
      const removedColl = prevColl.filter((id) => !nextColl.includes(id));
      for (const userId of removedColl) {
        const cfg = state.tagsConfigurations[userId];
        if (cfg) {
          removeMetadataElementIdFromConfigurationItemMap(
            cfg[UserInputTypes.Collaborators],
            metadataElementId,
          );
        }
      }
      for (const userId of addedColl) {
        ensureTagUserConfigurations(state, userId);
        addSalesforceItemToConfigurationItemMap(
          state.tagsConfigurations[userId][UserInputTypes.Collaborators],
          item,
        );
        if (!state.usersWithMetadataList.some((x) => x.userId === userId)) {
          state.usersWithMetadataList.push({ userId });
        }
      }
    },
    addItemsToParserItemsMap: (
      state,
      action: PayloadAction<{ crmOrgId: string; newParserItemsMap: DocumentationItemsMap }>,
    ) => {
      const { crmOrgId, newParserItemsMap } = action.payload;

      if (!state.parserItemsMap) {
        state.parserItemsMap = {};
      }

      if (!state.parserItemsMap[crmOrgId]) {
        state.parserItemsMap[crmOrgId] = {} as DocumentationItemsMap;
      }

      Object.keys(newParserItemsMap).forEach((key) => {
        const typedKey = key as keyof DocumentationItemsMap;
        const currentItems = state.parserItemsMap?.[crmOrgId]?.[typedKey] ?? [];
        const newItems = newParserItemsMap[typedKey];
        state.parserItemsMap![crmOrgId][typedKey] = uniq([...currentItems, ...newItems]);
      });
    },
  },
});

export const {
  applyUserInputsFromTagsAndUsersApi,
  setTagsList,
  setUsersWithMetadataList,
  setTagConfigurations,
  addTag,
  updateTag,
  deleteTag,
  clearTagConfigurations,

  generateTagsAndUsersSummary,
  setParserItemsMap,
  addItemsToParserItemsMap,

  setSingleConfigurationUserInputs,
  setUserInputsByMetadataId,
  deleteTagFromConfigurationUserInputs,
  patchUserInputsMetadataForSavedConfiguration,
} = userInputsSlice.actions;

export const selectTagsList = (state: RootState) => state.userInputs.tagsList;
export const selectUsersWithMetadataList = (state: RootState) =>
  state.userInputs.usersWithMetadataList;
export const selectTagById = (tagId: string) => (state: RootState) =>
  state.userInputs.tagsList.find((tag) => tag.id === tagId);

export const selectTagConfigurations = (tagId: string) => (state: RootState) =>
  state.userInputs.tagsConfigurations[tagId];

export const selectUserInputsByMetadataId = (metadataId: string) => (state: RootState) =>
  state.userInputs.userInputsByMetadataId?.[metadataId];

export const selectCountSummaryByCrmOrgId = (crmOrgId: string) => (state: RootState) =>
  state.userInputs.tagsAndUsersCountSummary?.[crmOrgId];

export default userInputsSlice.reducer;
